import { useEffect, useState, useRef } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import type { PaymentRequest } from '@stripe/stripe-js';

interface PaymentRequestButtonProps {
    amount: number; // in cents
    onPaymentSuccess: (paymentMethodId: string) => Promise<void>;
    onPaymentError: (error: string) => void;
    disabled?: boolean;
    label?: string;
}

export default function PaymentRequestButton({
    amount,
    onPaymentSuccess,
    onPaymentError,
    disabled = false,
    label = 'Movie Ticket Booking'
}: PaymentRequestButtonProps) {
    const stripe = useStripe();
    const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);

    // Use refs to hold the latest callback functions
    // This allows us to omit them from the useEffect dependency array
    // preventing the PaymentRequest from being re-initialized unnecessarily
    const onPaymentSuccessRef = useRef(onPaymentSuccess);
    const onPaymentErrorRef = useRef(onPaymentError);

    useEffect(() => {
        onPaymentSuccessRef.current = onPaymentSuccess;
        onPaymentErrorRef.current = onPaymentError;
    }, [onPaymentSuccess, onPaymentError]);

    useEffect(() => {
        if (!stripe || amount <= 0) {
            console.log('[PaymentRequestButton] Not initializing:', { stripe: !!stripe, amount });
            return;
        }

        console.log('[PaymentRequestButton] Initializing with amount:', amount / 100, 'EUR');

        const pr = stripe.paymentRequest({
            country: 'IT',
            currency: 'eur',
            total: {
                label: label,
                amount: amount,
            },
            requestPayerName: true,
            requestPayerEmail: true,
        });

        // Check if the browser supports payment request (Apple Pay, Google Pay, etc.)
        pr.canMakePayment().then(result => {
            console.log('[PaymentRequestButton] canMakePayment result:', result);
            if (result) {
                console.log('[PaymentRequestButton] ✅ Wallet payment available!', result);
                setPaymentRequest(pr);
            } else {
                console.log('[PaymentRequestButton] ❌ No wallet payment available');
            }
        }).catch(err => {
            console.error('[PaymentRequestButton] canMakePayment error:', err);
        });

        pr.on('paymentmethod', async (e) => {
            console.log('[PaymentRequestButton] Payment method received:', e.paymentMethod.id);

            try {
                console.log('[PaymentRequestButton] Processing payment confirmation...');
                // Wait for the parent component to confirm the payment
                await onPaymentSuccessRef.current(e.paymentMethod.id);

                // If successful, close the wallet UI with success
                console.log('[PaymentRequestButton] Payment confirmed, closing wallet UI');
                e.complete('success');
            } catch (error: any) {
                console.error('[PaymentRequestButton] Payment error:', error);
                // If failed, close the wallet UI with failure
                e.complete('fail');
                onPaymentErrorRef.current(error.message || 'Payment failed');
            }
        });

        return () => {
            pr.off('paymentmethod');
        };
    }, [stripe, amount, label]); // Callbacks removed from dependencies

    if (!paymentRequest || disabled) {
        console.log('[PaymentRequestButton] Not rendering:', { paymentRequest: !!paymentRequest, disabled });
        return null;
    }

    console.log('[PaymentRequestButton] ✅ Rendering wallet button');

    return (
        <div className="mb-6">
            <PaymentRequestButtonElement
                options={{ paymentRequest }}
            />

            <div className="flex items-center my-4">
                <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                <span className="px-4 text-sm text-gray-500 dark:text-gray-400">OR</span>
                <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
            </div>
        </div>
    );
}
